import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@reflex/ui/components/ui/alert';
import { Button } from '@reflex/ui/components/ui/button';
import { getErrorReporter } from '@/lib/error-reporter';

interface AgentStreamErrorBoundaryProps {
  children: ReactNode;
}

interface AgentStreamErrorBoundaryState {
  error: Error | null;
}

/**
 * Surfaces render errors from `AgentStreamView` as a visible alert instead of
 * a blank pane. Callers should key this boundary on the agent id so it
 * remounts (and clears any stale error) when switching agents.
 */
export class AgentStreamErrorBoundary extends Component<
  AgentStreamErrorBoundaryProps,
  AgentStreamErrorBoundaryState
> {
  state: AgentStreamErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): AgentStreamErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    getErrorReporter().captureException(error);
    console.error('[agent-stream] render error', error, info);
  }

  handleRetry = () => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="flex h-full items-center justify-center p-6">
          <Alert variant="destructive" className="max-w-md" data-testid="agent-stream-error">
            <AlertTriangle className="icon-md" />
            <AlertTitle>Couldn't load this agent</AlertTitle>
            <AlertDescription className="space-y-3">
              <p>The agent view crashed while rendering. The error has been reported.</p>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" onClick={this.handleRetry}>
                  <RefreshCw className="icon-sm" />
                  Try again
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => window.location.reload()}
                >
                  Reload page
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      );
    }
    return this.props.children;
  }
}
